import { useEffect, useState } from 'react';

import {
  Accordion,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  formatPhoneNumberForTextInputV2,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';
import { useDispatch } from 'react-redux';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useModeContext, UserMode } from '~/lib/contexts/ModeContext';
import {
  getDuplicateErrorForEmailAndPhone,
  getEditPayloadData,
  getPayloadDataForAdmin,
  reformatErrorMessageForDisplay,
  shouldDisableSaveButton,
  validateAdminErrors,
  validateUserErrors,
} from '~/lib/util/profile.util';
import { CacheTag } from '~/store/services/CacheTag';
import {
  providersApi,
  useAddUserMutation,
  useDeleteProviderAvatarByIdMutation,
  useEditAdminProviderByIdMutation,
  useEditProviderByIdMutation,
  useGetProviderAvatarByIdQuery,
} from '~/store/services/providers';

import ProfileAddEditForm from './AddEditProfileForm';
import DiscardChangesModalV2 from '../DiscardChangesModalV2';
import DeleteConfirmModal from './DeleteConfirmModal';
import {
  AVATAR_DELETE_CONFIRMATION_MODAL_TITLE,
  AVATAR_DELETE_WARNING_MESSAGE,
} from '~/lib/constants/profile.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface ProfileEditProfileAccordionProps {
  onDismissEditProfileAccordion?: () => void;
  isExpanded: boolean;
  setIsExpanded?: (expanded: boolean) => void;
  profileData?: GetProviderByIdResponse;
}

interface UniversalSelectOption {
  value: string;
  label: string;
}

const AddEditProfileAccordion = ({
  onDismissEditProfileAccordion,
  isExpanded,
  setIsExpanded,
  profileData,
}: ProfileEditProfileAccordionProps) => {
  const { createToast } = useToastMessage();
  const dispatch = useDispatch();
  const { providerTypePkTransition: isProviderTypePkTransitionEnabled } =
    useFlags<LDFlags>();

  const TOP_LEVEL_ID = 'edit-profile-accordion';
  const ACCORDION_WARNING_MESSAGE_ID = `${TOP_LEVEL_ID}__warning__message-bar`;
  const isAddForm = !profileData?.userId;
  const providerCredentials = profileData?.credentialsDetails?.map(
    (item) => item.code,
  );
  const providerPronouns = profileData?.pronounsDetails?.map(
    (item) => item.code,
  );
  const providerLanguages = profileData?.languagesDetails?.map(
    (item) => item.externalId,
  );
  const supervisorFullName = `${profileData?.supervisor?.firstName}`;

  const [editProvider, editProviderResult] = useEditProviderByIdMutation();
  const [editProviderByAdmin, editProviderResultByAdmin] =
    useEditAdminProviderByIdMutation();
  const [deleteProviderAvatar, deleteProviderAvatarResult] =
    useDeleteProviderAvatarByIdMutation();

  const { data: avatarData } = useGetProviderAvatarByIdQuery(
    {
      providerExternalId: profileData?.userId,
    },
    {
      skip: !profileData?.userId,
    },
  );

  const [addUser, addUserResult] = useAddUserMutation();
  const { trackEvent } = useAnalytics();

  const [editForm, setEditForm] = useState<EditProfile>({
    accountStatus: profileData?.userStatus,
    appAccess: profileData?.appAccess,
    avatarSrc: avatarData?.url || '',
    capacity: profileData?.capacity?.code,
    connectWithMe: profileData?.connectWithMe,
    credentials: providerCredentials || [],
    displayFirstName: profileData?.displayFirstName || '',
    displayLastName: profileData?.displayLastName || '',
    displayMiddleName: profileData?.displayMiddleName || '',
    displaySuffix: profileData?.displaySuffix || '',
    email: profileData?.email,
    firstName: profileData?.firstName,
    hoursPerWeek: profileData?.hoursPerWeek,
    languages: providerLanguages || [],
    lastName: profileData?.lastName,
    middleName: profileData?.middleName,
    nationalProviderIdentifier: profileData?.nationalProviderIdentifier,
    otherPronounText: profileData?.otherPronounText,
    permissions: profileData?.permissions,
    phoneNumber: formatPhoneNumberForTextInputV2(profileData?.phoneNumber),
    professionalExperience: profileData?.professionalExperience,
    pronouns: providerPronouns || [],
    pronunciation: profileData?.pronunciation,
    providerType: isProviderTypePkTransitionEnabled
      ? profileData?.providerTypeDetails?.code
      : profileData?.providerTypeDetails?.externalId,
    roles: profileData?.roles,
    stateOfResidence: profileData?.stateOfResidenceDetails?.code,
    suffix: profileData?.suffix,
    supervisor: profileData?.supervisor?.externalId,
    timezone: profileData?.timeZoneDetails?.id,
    userId: profileData?.userId,
    userStatus: profileData?.userStatus,
  });

  const displayNameValue = `${
    editForm?.displayFirstName ||
    editForm?.displayMiddleName ||
    editForm?.displayLastName ||
    editForm?.displaySuffix
  }`;
  const [showDisplayName, setShowDisplayName] =
    useState<boolean>(!!displayNameValue);

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState<boolean>(false);
  const [isUnsavedProfileChanges, setIsUnsavedProfileChanges] =
    useState<boolean>(false);
  const [shouldScrollToWarningToast, setShouldScrollToWarningToast] =
    useState<boolean>(false);
  const [providerTypeName, setProviderTypeName] = useState<string>(null);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);

  const isSubmitError =
    addUserResult?.isError ||
    editProviderResult?.isError ||
    editProviderResultByAdmin?.isError;

  const duplicateError =
    getDuplicateErrorForEmailAndPhone(addUserResult?.error) ||
    getDuplicateErrorForEmailAndPhone(editProviderResult?.error) ||
    getDuplicateErrorForEmailAndPhone(editProviderResultByAdmin?.error);

  const isLoading =
    addUserResult?.isLoading ||
    editProviderResult?.isLoading ||
    editProviderResultByAdmin?.isLoading;
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;
  const [requiredFieldErrors, setRequiredFieldErrors] =
    useState<RequiredFieldErrors>({
      capacity: false,
      displayFirstName: false,
      displayLastName: false,
      email: false,
      firstName: false,
      hoursPerWeek: false,
      lastName: false,
      otherPronounText: false,
      phoneNumber: false,
      providerType: false,
    });

  useEffect(() => {
    setEditForm({
      accountStatus: profileData?.userStatus,
      appAccess: profileData?.appAccess,
      avatarSrc: avatarData?.url || '',
      capacity: profileData?.capacity?.code,
      connectWithMe: profileData?.connectWithMe,
      credentials: providerCredentials || [],
      displayFirstName: profileData?.displayFirstName || '',
      displayLastName: profileData?.displayLastName || '',
      displayMiddleName: profileData?.displayMiddleName || '',
      displaySuffix: profileData?.displaySuffix || '',
      email: profileData?.email || '',
      firstName: profileData?.firstName || '',
      hoursPerWeek: profileData?.hoursPerWeek,
      languages: providerLanguages || [],
      lastName: profileData?.lastName || '',
      middleName: profileData?.middleName || '',
      nationalProviderIdentifier: profileData?.nationalProviderIdentifier || '',
      otherPronounText: profileData?.otherPronounText || '',
      permissions: profileData?.permissions,
      phoneNumber: formatPhoneNumberForTextInputV2(
        profileData?.phoneNumber || '',
      ),
      professionalExperience: profileData?.professionalExperience,
      pronouns: providerPronouns || [],
      pronunciation: profileData?.pronunciation || '',
      providerType: isProviderTypePkTransitionEnabled
        ? profileData?.providerTypeDetails?.code
        : profileData?.providerTypeDetails?.externalId,
      roles: profileData?.roles,
      stateOfResidence: profileData?.stateOfResidenceDetails?.code,
      suffix: profileData?.suffix || '',
      supervisor: profileData?.supervisor?.externalId,
      timezone: profileData?.timeZoneDetails?.id,
      userId: profileData?.userId,
      userStatus: profileData?.userStatus,
    });
  }, [profileData, avatarData]);

  useEffect(() => {
    if (shouldScrollToWarningToast) {
      const accordionWarning = document.getElementById(
        ACCORDION_WARNING_MESSAGE_ID,
      );
      if (accordionWarning) {
        accordionWarning.scrollIntoView({ behavior: 'smooth' });
        setShouldScrollToWarningToast(false);
      }
    }
  }, [
    shouldScrollToWarningToast,
    document.getElementById(ACCORDION_WARNING_MESSAGE_ID),
  ]);

  const onResetAndClose = (): void => {
    setIsUnsavedProfileChanges(false);
    setIsUnsavedChangesModalOpen(false);
    onDismissEditProfileAccordion();
  };

  const handleCancelAndClose = (): void => {
    if (isUnsavedProfileChanges || isSubmitError) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onResetAndClose();
    }
  };

  const handleChange = (field: string, value: string | string[]) => {
    setIsUnsavedProfileChanges(true);
    setEditForm((prevState) => {
      return { ...prevState, [field]: value };
    });

    if (requiredFieldErrors[field]) {
      const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
      if (!isEmpty) {
        setRequiredFieldErrors((prevErrors) => ({
          ...prevErrors,
          [field]: false,
        }));
      }
    }
  };

  const handleSelectChange =
    (field: string) =>
    (selectedOptions: UniversalSelectOption | UniversalSelectOption[]) => {
      if (Array.isArray(selectedOptions)) {
        const values = selectedOptions?.map((option) => option.value);
        handleChange(field, values);
      } else {
        const selectedValue = selectedOptions ? selectedOptions?.value : null;
        handleChange(field, selectedValue);
        if (field === 'capacity') {
          if (selectedValue === 'PARTTIME') {
            handleChange('hoursPerWeek', '40');
          } else {
            handleChange('hoursPerWeek', '');
          }
        } else if (field === 'providerType') {
          setProviderTypeName(selectedOptions?.label);
        }
      }
    };

  const handleDisplayButtonClick = (value: boolean) => {
    setShowDisplayName(value);
    handleChange('displayFirstName', '');
    handleChange('displayLastName', '');
    handleChange('displayMiddleName', '');
    handleChange('displaySuffix', '');
  };

  const shouldDisableSave = shouldDisableSaveButton(editForm);

  const invalidateCache = (providerExternalId: string) => {
    dispatch(
      providersApi.util.invalidateTags([
        { id: providerExternalId, type: CacheTag.ProfileInfo },
      ]),
    );
  };

  const showToast = (message: string, type: 'warning' | 'success') => {
    createToast({
      icon: FAIconName.checkRegular,
      id: type === 'warning' ? 'partial-data_toast' : 'save-profile_toast',
      text: message,
      variant: type,
    });
    onResetAndClose();
  };

  const handleProviderEdit = async (
    editData: EditProviderFields,
    providerExternalId: string,
  ) => {
    return editProvider({
      editData,
      providerExternalId,
    })
      .unwrap()
      .then(() => {
        showToast('User profile updated.', 'success');
        invalidateCache(providerExternalId);
      })
      .catch(() => {
        if (isAdmin) {
          showToast('User profile partially updated.', 'warning');
          invalidateCache(providerExternalId);
        } else {
          setShouldScrollToWarningToast(true);
        }
      });
  };
  const handleAdminEdit = async (
    editData: AddEditProviderRequestBody,
    providerExternalId: string,
  ) => {
    return editProviderByAdmin({
      editData,
      providerExternalId,
    })
      .unwrap()
      .then(() => {
        const editPayloadData = getEditPayloadData(editForm);
        return handleProviderEdit(editPayloadData, providerExternalId);
      })
      .catch(() => {
        setShouldScrollToWarningToast(true);
      });
  };
  const handleTrackEvent = () => {
    if (isAddForm && isAdmin) {
      trackEvent(ProfileEventsAdmin.addUserModalSaveChangesButtonClicked, {
        source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
      });
    } else {
      trackEvent(
        isAdmin
          ? ProfileEventsAdmin.adminEditUserModalSaveChangesButtonClicked
          : ProfileEventsMaud.maudEditUserModalSaveChangesButtonClicked,
        {
          source: isAdmin
            ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
            : AnalyticsSourceValues.PROFILE_TAB_MAUD,
        },
      );
    }
  };

  const handleSave = () => {
    handleTrackEvent();
    const addEditAdminData = getPayloadDataForAdmin(
      isAddForm,
      editForm,
      providerTypeName,
      isProviderTypePkTransitionEnabled,
    );
    const editData = getEditPayloadData(editForm);

    const adminErrors = validateAdminErrors(editForm, showDisplayName);
    const validateAndSetErrors = (errors: RequiredFieldErrors) => {
      const hasErrors = Object.values(errors).some((error) => error);
      if (hasErrors) {
        setRequiredFieldErrors(errors);
      }
      return hasErrors;
    };

    if (isAddForm) {
      if (validateAndSetErrors(adminErrors)) return;

      addUser(addEditAdminData)
        .unwrap()
        .then((response) => {
          const providerExternalId = response?.id;
          editProvider({ editData, providerExternalId })
            .unwrap()
            .then(() => {
              showToast('New user added.', 'success');
            })
            .catch(() => {
              showToast(
                'New user created with partial data. Please check to update.',
                'warning',
              );
            });
        })
        .catch(() => {
          setShouldScrollToWarningToast(true);
        });
    } else {
      const errors = isAdmin
        ? adminErrors
        : validateUserErrors(editForm, showDisplayName);

      if (validateAndSetErrors(errors)) return;

      if (isAdmin) {
        handleAdminEdit(addEditAdminData, profileData?.userId);
      } else {
        handleProviderEdit(editData, profileData?.userId);
      }
    }
  };

  const handleDeleteAvatar = () => {
    deleteProviderAvatarResult.reset();
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleAvatarDeleteConfirm = () => {
    deleteProviderAvatar({
      providerExternalId: profileData?.userId,
    })
      .unwrap()
      .then(() => {
        setIsDeleteConfirmationModalOpen(false);
      })
      .catch(() => {
        setIsDeleteConfirmationModalOpen(true);
      });
  };

  const footerContent = (
    <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
      <FireflyButton
        id={`${TOP_LEVEL_ID}__cancel-button`}
        leftIcon={FAIconName.XmarkRegular}
        onClick={handleCancelAndClose}
        variant="secondary"
      >
        Cancel
      </FireflyButton>
      <FireflyButton
        id={`${TOP_LEVEL_ID}__save-button`}
        isDisabled={!isUnsavedProfileChanges || shouldDisableSave}
        isLoading={isLoading}
        leftIcon={FAIconName.checkRegular}
        onClick={handleSave}
      >
        Save changes
      </FireflyButton>
    </ChakraFlex>
  );

  return (
    <>
      <DiscardChangesModalV2
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={onResetAndClose}
      />
      <Accordion
        dismissMenu={handleCancelAndClose}
        footer={footerContent}
        id={TOP_LEVEL_ID}
        isExpanded={isExpanded}
        panelContentMaxHeight={ACCORDION_HEIGHT_CALCULATED}
        panelMaxWidth={ACCORDION_WIDTH_LARGE}
        setIsExpanded={setIsExpanded}
        title={isAddForm ? 'Add user profile ' : 'Edit user profile'}
      >
        <ChakraFlex
          flexDirection="column"
          gridGap="24px"
          minWidth={ACCORDION_WIDTH_LARGE}
          padding="24px"
        >
          <ProfileAddEditForm
            editForm={editForm}
            handleChange={handleChange}
            handleDisplayButtonClick={handleDisplayButtonClick}
            handleSelectChange={handleSelectChange}
            isAddForm={isAddForm}
            onDeleteAvatar={handleDeleteAvatar}
            requiredFieldErrors={requiredFieldErrors}
            showDisplayName={showDisplayName}
            supervisorFullName={supervisorFullName}
          />
          {isSubmitError && (
            <ToastMessage
              icon={FAIconName.exclamationTriangleRegular}
              id={`${TOP_LEVEL_ID}__warning`}
              isFullWidth
              text={
                reformatErrorMessageForDisplay(duplicateError?.message) ||
                "We're sorry, we were unable to save your changes. Please try again."
              }
              variant="critical"
            />
          )}
        </ChakraFlex>
      </Accordion>
      <DeleteConfirmModal
        buttonTitle={AVATAR_DELETE_CONFIRMATION_MODAL_TITLE}
        isErrorToastVisible={deleteProviderAvatarResult?.isError}
        isOpen={isDeleteConfirmationModalOpen}
        isPrimaryButtonDisabled={deleteProviderAvatarResult?.isLoading}
        onConfirmClick={handleAvatarDeleteConfirm}
        setIsOpen={setIsDeleteConfirmationModalOpen}
        subTitle={AVATAR_DELETE_WARNING_MESSAGE}
        title={AVATAR_DELETE_CONFIRMATION_MODAL_TITLE}
      />
    </>
  );
};

export default AddEditProfileAccordion;
